import {
  Colors,
  Flex,
  HeaderContainer,
  HeaderIcon,
  NaviIconSearch,
  RemoteImage,
  Typography,
  useSafeArea,
  useScreenSize,
} from '@banhala/ui-kit';
import { CartIcon } from 'shared/src/component';
import { useOpenDeeplink } from 'shared/src/hooks';
import { useHomeIcons } from './queries/useHomeIcons';
import { isNil, isNotNil, useNavigator } from 'shared/src/utils';
import React, { useCallback, useContext, useMemo } from 'react';
import Svg, { Circle, Path } from 'react-native-svg';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { HomeScrollContext } from '../../context/HomeScrollContext';
import { useConfigs } from '../../../../../../../shared/src/queries';
import { useActiveHomeTab } from '../../hooks/useActiveHomeTab';
import { useLogClickSearch } from './hooks/useLogClickSearch';
import { useLogClickBenefits } from './hooks/useLogClickBenefits';
import { useLogClickSearchPromotion } from './hooks/useLogClickSearchPromotion';

const PointHeaderIcon: React.FC<{ onPress: () => void }> = props => {
  return (
    <HeaderIcon onPress={props.onPress}>
      <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <Circle cx="12" cy="12" r="12" fill="#FFD350" />
        <Path
          d="M13.2222 6.44493C15.3132 6.44498 17.0081 8.14005 17.0083 10.2311C17.0083 12.3222 15.3133 14.0181 13.2222 14.0182H11.3062V17.5641H8.81201V6.44493H13.2222ZM11.2798 8.62755V11.8346H12.8384C13.7241 11.8346 14.4429 11.1167 14.4429 10.2311C14.4428 9.34545 13.724 8.62755 12.8384 8.62755H11.2798Z"
          fill="white"
        />
      </Svg>
    </HeaderIcon>
  );
};

const ABLYLogo = () => {
  return (
    <Svg width="80" height="16" viewBox="0 0 80 16" fill="none">
      <Path
        d="M32.9811 0.0351562C35.0943 0.116157 37.0748 1.88043 37.0748 4.03125C37.0777 4.06614 37.2939 6.79561 35.025 7.5332C34.9692 7.55245 34.8733 7.57359 34.8209 7.59082C34.8323 7.59312 34.8437 7.59702 34.8551 7.59961C36.5889 7.98567 37.897 9.55149 37.8971 11.4121V12.0381C37.8971 14.1863 36.1567 15.9375 34.027 15.9375L29.0211 15.9355H24.8385L24.8356 0.0244141L29.0182 0.0253906L29.0455 0.0244141L32.9811 0.0351562ZM17.5153 15.9346H13.8405L12.401 12.4922H5.91565L4.4762 15.9346H0.801392L7.48694 0H10.8297L17.5153 15.9346ZM50.7213 12.7656H58.9879V15.9346H47.067V0H50.7213V12.7656ZM71.7028 5.92676L75.2399 0H79.1989L73.5299 9.64258V15.9346H69.8756V9.64258L64.2067 0H68.1656L71.7028 5.92676ZM28.5358 9.7168V13.2158H31.4078V13.2148H32.3268C33.2647 13.2147 34.1832 12.4409 34.1832 11.4951V11.4375C34.1832 10.4914 33.2647 9.71789 32.3268 9.71777H31.4078V9.7168H28.5358ZM7.22131 9.36914H11.0953L9.16956 4.74219L7.22131 9.36914ZM28.5358 2.74512V6.24414H31.7535C32.6912 6.24414 33.4584 5.47042 33.4586 4.52441V4.4668C33.4586 3.52064 32.6913 2.74609 31.7535 2.74609H31.4205V2.74512H28.5358Z"
        fill="#1F1F1F"
      />
    </Svg>
  );
};

const HomeHeaderContent = () => {
  const { data: homeIcons } = useHomeIcons();
  const { data: config } = useConfigs();
  const { width: screenWidth } = useScreenSize();
  const { openDeeplink } = useOpenDeeplink();

  const homeScroll = useContext(HomeScrollContext);
  const headerHeight = useSharedValue(0);
  const { top: safeAreaTop } = useSafeArea();
  const activeHomeTab = useActiveHomeTab();
  const { data: configs } = useConfigs();
  const { navigator } = useNavigator();

  const screenName = useMemo(
    () => activeHomeTab?.screen_name ?? 'TODAY',
    [activeHomeTab?.screen_name],
  );

  const logClickSearch = useLogClickSearch(screenName);
  const logClickBenefits = useLogClickBenefits(screenName);
  const logClickSearchPromotion = useLogClickSearchPromotion(screenName);

  const animatedStyle = useAnimatedStyle(() => {
    // dependency tracking을 위해 early return 전에 항상 .value 읽기
    const visible = homeScroll?.headerVisible.value ?? 1;
    const h = headerHeight.value;

    if (!homeScroll || h === 0) {
      return { width: screenWidth };
    }
    return {
      width: screenWidth,
      height: interpolate(visible, [0, 1], [safeAreaTop, h]),
      overflow: 'hidden' as const,
    };
  });

  const onPressLogo = useCallback(() => {
    if (isNil(homeIcons.logo)) return;
    if (isNotNil(homeIcons.logo.deeplink)) {
      openDeeplink(homeIcons.logo.deeplink);
    }
  }, [homeIcons.logo, openDeeplink]);

  const onPressPoint = useCallback(() => {
    if (isNotNil(configs) && isNotNil(configs.benefits_deeplink)) {
      openDeeplink(configs.benefits_deeplink);
      logClickBenefits();
    }
  }, [configs, openDeeplink, logClickBenefits]);

  const onPressSearch = useCallback(() => {
    logClickSearch();
    navigator.moveToSearch();
  }, [logClickSearch, navigator]);

  const onPressSearchIcon = useCallback(() => {
    const { deeplink, text } = configs.search_placeholder;
    if (isNotNil(deeplink)) {
      logClickSearchPromotion({ title: text, deeplink });
      openDeeplink(deeplink);
    } else {
      logClickSearch();
      navigator.moveToSearch();
    }
  }, [
    configs.search_placeholder,
    logClickSearch,
    logClickSearchPromotion,
    navigator,
    openDeeplink,
  ]);

  return (
    <Flex alignItems={'center'}>
      <Animated.View style={animatedStyle}>
        <Flex
          onLayout={e => {
            if (headerHeight.value === 0) {
              headerHeight.value = e.nativeEvent.layout.height;
            }
          }}
        >
          <HeaderContainer justifyContent={'space-between'}>
            <Flex justifyContent={'center'} padding={{ left: 12 }}>
              {isNotNil(homeIcons.logo) &&
              isNotNil(homeIcons.logo.image_url) ? (
                <Flex onPress={onPressLogo}>
                  <RemoteImage
                    uri={homeIcons.logo.image_url}
                    width={homeIcons.logo.image_width}
                    height={homeIcons.logo.image_height}
                  />
                </Flex>
              ) : (
                <Flex padding={{ left: 4 }}>
                  <ABLYLogo />
                </Flex>
              )}
            </Flex>
            <Flex.Row padding={{ right: 8 }}>
              <PointHeaderIcon onPress={onPressPoint} />
              <CartIcon />
            </Flex.Row>
          </HeaderContainer>
          <Flex padding={{ horizontal: 12, bottom: 4 }}>
            <Flex.Row
              height={40}
              borderRadius={'small'}
              backgroundColor={'bg_secondary'}
              padding={{ horizontal: 12 }}
              alignItems={'center'}
              justifyContent={'space-between'}
              onPress={onPressSearch}
            >
              <Typography color={'content_secondary'} type={'body2'}>
                {config?.search_placeholder?.text ?? ''}
              </Typography>
              <Flex
                onPress={onPressSearchIcon}
                hitSlop={{ left: 4, right: 4, top: 4, bottom: 4 }}
              >
                <NaviIconSearch size={20} fill={Colors.content_secondary} />
              </Flex>
            </Flex.Row>
          </Flex>
        </Flex>
      </Animated.View>
    </Flex>
  );
};

export const HomeHeader = () => {
  return (
    <React.Suspense
      fallback={
        <HeaderContainer justifyContent={'space-between'}>
          <Flex justifyContent={'center'} padding={{ left: 12 }}>
            <Flex padding={{ left: 4 }}>
              <ABLYLogo />
            </Flex>
          </Flex>
          <Flex.Row padding={{ right: 8 }}>
            <PointHeaderIcon onPress={() => {}} />
            <CartIcon />
          </Flex.Row>
        </HeaderContainer>
      }
    >
      <HomeHeaderContent />
    </React.Suspense>
  );
};
